import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, MessageSquareText, Target, Sparkles, Compass, TrendingUp } from "lucide-react";

const COUNTRIES = [
  { code: "AU", flag: "🇦🇺", name: "Australia", variant: "Australian English", desc: "Workplace culture, GP visits, Medicare, rental inspections." },
  { code: "CA", flag: "🇨🇦", name: "Canada", variant: "Canadian English", desc: "Family doctors, hydro bills, workplace etiquette, winter small talk." },
  { code: "UK", flag: "🇬🇧", name: "United Kingdom", variant: "British English", desc: "NHS registration, council tax, tenancy agreements, polite indirectness." },
];

export default function Landing() {
  return (
    <div>
      {/* Hero */}
      <section className="rle-gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="rle-eyebrow">Real English • Real Situations</span>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                Learn the English you need for <span className="text-emerald-700">real life</span>.
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
                Practise real conversations, learn useful English and prepare for everyday situations in Australia, Canada and the UK.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register"><Button size="lg" className="rounded-full bg-emerald-700 hover:bg-emerald-800 h-12 px-6" data-testid="hero-start-learning-free-btn">Start Learning Free <ArrowRight size={18} className="ml-1"/></Button></Link>
                <Link to="/how-it-works"><Button size="lg" variant="outline" className="rounded-full h-12 px-6" data-testid="hero-how-it-works-btn">See How It Works</Button></Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {COUNTRIES.map((c) => (
                  <Link key={c.code} to={`/country/${c.code}`} className="rle-chip" data-testid={`hero-country-${c.code.toLowerCase()}-chip`}>
                    <span className="mr-2 text-base">{c.flag}</span>{c.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rle-card p-6 shadow-xl shadow-emerald-900/5">
                <span className="rle-eyebrow">Sample real-life mission</span>
                <h3 className="mt-2 font-display font-bold text-xl text-slate-900">Talk to your manager about a project delay</h3>
                <div className="mt-4 space-y-3">
                  <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">Manager</div>
                    <div className="text-sm text-slate-800">Hi — I saw a note about the report. How are we tracking?</div>
                  </div>
                  <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 ml-6">
                    <div className="text-xs text-emerald-700 mb-1">You</div>
                    <div className="text-sm text-slate-800">I wanted to give you a heads-up — there's been a small delay because…</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <span className="rle-chip">🇦🇺 Australian workplace</span>
                  <span>4–6 turns · ~5 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <Section eyebrow="Why English Ready" title="Practise the English you actually use.">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Target, title: "Real-life situations", body: "Rental inspections, GP appointments, project updates, interviews — not textbook drills." },
            { icon: Compass, title: "Country-specific English", body: "Australian, Canadian and British variants — vocabulary and culture that fit where you live." },
            { icon: Sparkles, title: "Personalised learning", body: "A plan built around your goals, level and available time." },
            { icon: MessageSquareText, title: "Speaking practice", body: "Voice practice is coming. Today, structured conversation scenarios build the muscle." },
            { icon: CheckCircle2, title: "Mistake Bank", body: "See your recurring mistakes turned into short focused practice." },
            { icon: TrendingUp, title: "Progress tracking", body: "See what you've completed, where you're improving and what to try next." },
          ].map((f, i) => (
            <div key={i} className="rle-card p-6" data-testid={`why-card-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><f.icon size={20}/></div>
              <h3 className="mt-4 font-display font-bold text-lg text-slate-900">{f.title}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <Section eyebrow="How it works" title="A structured path, not a random chatbot.">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { n: "01", t: "Tell us where you live", d: "Australia, Canada or the UK — we tailor the English variant." },
            { n: "02", t: "Take a short assessment", d: "8 quick questions estimate your practice level." },
            { n: "03", t: "Follow your plan", d: "Short lessons + real-life scenarios based on your goals." },
            { n: "04", t: "Track your progress", d: "Completed lessons, missions, and mistakes to review." },
          ].map((s, i) => (
            <div key={i} className="rle-card p-6" data-testid={`how-step-${i+1}`}>
              <div className="font-mono text-xs text-emerald-700">{s.n}</div>
              <h3 className="mt-2 font-display font-bold text-lg text-slate-900">{s.t}</h3>
              <p className="mt-2 text-slate-600 text-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Countries */}
      <Section eyebrow="Country-specific English" title="Choose your country.">
        <div className="grid md:grid-cols-3 gap-6">
          {COUNTRIES.map((c) => (
            <Link key={c.code} to={`/country/${c.code}`} className="rle-card p-6 group" data-testid={`country-card-${c.code.toLowerCase()}`}>
              <div className="text-4xl">{c.flag}</div>
              <h3 className="mt-3 font-display font-bold text-xl text-slate-900">{c.name}</h3>
              <div className="text-emerald-700 text-sm font-medium">{c.variant}</div>
              <p className="mt-3 text-slate-600 text-sm">{c.desc}</p>
              <div className="mt-4 text-sm font-medium text-emerald-700 flex items-center gap-1 group-hover:gap-2 transition-all">Explore {c.name} <ArrowRight size={16}/></div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Pricing preview */}
      <Section eyebrow="Simple, honest pricing" title="Start free. Upgrade when you're ready.">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { code: "explore", name: "Explore", price: "A$0", tag: "Free", bullets: ["1 initial assessment", "1 starter lesson", "1 real-life scenario", "Mistake Bank preview"] },
            { code: "essential", name: "Essential", price: "A$9.99", tag: "/month", bullets: ["Full lesson library", "All real-life situations", "Limited AI practice (40 turns/mo)", "Basic Mistake Bank"] },
            { code: "complete", name: "Complete", price: "A$19.99", tag: "/month · Most features", highlight: true, bullets: ["Everything in Essential", "Higher AI allowance", "Workplace & interview practice", "Full Mistake Bank"] },
          ].map((p) => (
            <div key={p.code} className={`rle-card p-6 ${p.highlight ? "ring-2 ring-emerald-700" : ""}`} data-testid={`pricing-tile-${p.code}`}>
              {p.highlight && <div className="text-xs font-semibold text-emerald-700 mb-2">MOST FEATURES</div>}
              <div className="font-display font-bold text-xl text-slate-900">{p.name}</div>
              <div className="mt-2"><span className="text-3xl font-display font-extrabold text-slate-900">{p.price}</span><span className="text-sm text-slate-500 ml-1">{p.tag}</span></div>
              <ul className="mt-4 space-y-2 text-sm text-slate-700">
                {p.bullets.map((b, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-700 mt-0.5"/>{b}</li>)}
              </ul>
              <Link to="/pricing" className="mt-6 block"><Button variant={p.highlight ? "default" : "outline"} className={`w-full rounded-full ${p.highlight ? "bg-emerald-700 hover:bg-emerald-800" : ""}`} data-testid={`pricing-cta-${p.code}`}>See details</Button></Link>
            </div>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl">Ready to practise real-life English?</h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">Free to start. No credit card required.</p>
          <Link to="/register"><Button size="lg" className="mt-8 rounded-full bg-emerald-500 hover:bg-emerald-600 h-12 px-8" data-testid="footer-cta-btn">Start Learning Free <ArrowRight size={18} className="ml-1"/></Button></Link>
        </div>
      </section>
    </div>
  );
}

function Section({ eyebrow, title, children }) {
  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="rle-eyebrow">{eyebrow}</span>
          <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-slate-900 tracking-tight">{title}</h2>
        </div>
        {children}
      </div>
    </section>
  );
}
